// Top-level Scenarios route container (founder ratification 2026-07-31):
// scenario authoring moved off the agent hub onto the rail's Library group,
// agent-scoped exactly like /personas — `agent_id` stays a query filter,
// never a path parent. Scenario sets are the second tab of the same screen
// (`?tab=sets`); the retired `/agents/$agentId/scenarios` and
// `/agents/$agentId/scenario-sets` routes redirect here carrying `?agent=`.
// This container owns the URL state: the tab facet, the `?scenario=` /
// `?scenarioSet=` drawer subjects, and the `?new=` create drawers. A scope
// switch with a drawer open strips the subject (a stale drawer never survives
// into another agent's view).
import { useCallback, useEffect, useRef } from "react";

import {
  parseScenariosSearch,
  type ScenariosSearch,
  type ScenariosTab,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import {
  useWorkingScope,
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { Button, Page, PageHeader, TabPanel, Tabs, Text } from "../ui";
import { ScenariosList } from "./scenarios/ScenariosList";
import { ScenarioSetsList } from "./scenarioSets/ScenarioSetsList";

type ScenariosSearchState = ScenariosSearch & WorkingScopeSearch;

const scenarioTabs: Array<{ id: ScenariosTab; label: string }> = [
  { id: "scenarios", label: "Scenarios" },
  { id: "sets", label: "Scenario sets" },
];

export function Scenarios() {
  const { agentId } = useWorkingScope();
  const navigate = useTypedNavigate<ScenariosSearchState>();
  const search = useTypedSearch(withWorkingScopeSearch(parseScenariosSearch));
  const activeTab: ScenariosTab = search.tab ?? "scenarios";

  // Functional-merge navigation ONLY: patch this route's own params over the
  // previous search so the root-owned `?agent` scope always survives.
  const patchSearch = useCallback(
    (patch: Partial<ScenariosSearch>, replace: boolean) => {
      void navigate({
        replace,
        search: mergeSearch<ScenariosSearchState>(patch),
      });
    },
    [navigate],
  );

  // One drawer at a time (§4a), across both tabs: a tab switch drops the
  // other tab's open drawer. Closes are replaces so Back never reopens a
  // dismissed drawer.
  const closeEditors = useCallback(() => {
    patchSearch(
      { new: undefined, scenario: undefined, scenarioSet: undefined },
      true,
    );
  }, [patchSearch]);
  const onTabChange = useCallback(
    (tab: ScenariosTab) => {
      patchSearch(
        { new: undefined, scenario: undefined, scenarioSet: undefined, tab },
        false,
      );
    },
    [patchSearch],
  );
  const openCreate = useCallback(() => {
    patchSearch(
      {
        new: activeTab === "sets" ? "scenario-set" : "scenario",
        scenario: undefined,
        scenarioSet: undefined,
      },
      false,
    );
  }, [activeTab, patchSearch]);
  const openScenarioEdit = useCallback(
    (scenarioId: string) => {
      patchSearch(
        { new: undefined, scenario: scenarioId, scenarioSet: undefined },
        false,
      );
    },
    [patchSearch],
  );
  const openScenarioSetEdit = useCallback(
    (scenarioSetId: string) => {
      patchSearch(
        { new: undefined, scenario: undefined, scenarioSet: scenarioSetId },
        false,
      );
    },
    [patchSearch],
  );

  // A drawer subject never survives an agent-scope switch (the personas
  // precedent): any change of scope with a drawer open strips the params.
  const previousAgentIdRef = useRef(agentId);
  const hasOpenDrawer =
    search.new !== undefined ||
    search.scenario !== undefined ||
    search.scenarioSet !== undefined;
  useEffect(() => {
    if (previousAgentIdRef.current === agentId) {
      return;
    }
    previousAgentIdRef.current = agentId;
    if (hasOpenDrawer) {
      closeEditors();
    }
  }, [agentId, closeEditors, hasOpenDrawer]);

  // Scenario-set members are ordered scenarios of ONE agent, so creating a
  // set needs an agent scope (docs/09 §4); a scenario
  // itself creates fine under All agents through the editor's Agent pick.
  const canCreate = activeTab === "scenarios" || agentId !== undefined;

  return (
    <Page variant="workspace">
      <PageHeader
        actions={
          canCreate ? (
            <Button size="sm" onClick={openCreate}>
              {activeTab === "sets" ? "Create scenario set" : "Create scenario"}
            </Button>
          ) : undefined
        }
        tabs={
          <Tabs
            activeId={activeTab}
            ariaLabel="Scenarios"
            onSelect={(id) => {
              onTabChange(id as ScenariosTab);
            }}
            tabs={scenarioTabs}
          />
        }
      />
      <TabPanel activeId={activeTab} fill tabId="scenarios">
        <ScenariosList
          agentId={agentId}
          editingScenarioId={search.scenario ?? null}
          isCreateOpen={search.new === "scenario"}
          onCloseEditor={closeEditors}
          onOpenEdit={openScenarioEdit}
        />
      </TabPanel>
      <TabPanel activeId={activeTab} fill tabId="sets">
        {/* The create action is absent under All agents (a set's members are
            ordered scenarios of ONE agent); say so in-place rather than
            leaving the missing action unexplained. */}
        {agentId === undefined ? (
          <Text variant="muted">Select an agent to create a scenario set.</Text>
        ) : null}
        <ScenarioSetsList
          agentId={agentId}
          editingScenarioSetId={search.scenarioSet ?? null}
          // A hand-typed `?new=scenario-set` without an agent scope opens
          // nothing: the create form has no member options to offer.
          isCreateOpen={agentId !== undefined && search.new === "scenario-set"}
          onCloseEditor={closeEditors}
          onOpenEdit={openScenarioSetEdit}
        />
      </TabPanel>
    </Page>
  );
}
